import { Skeleton } from '@cargoflow/ui/components/skeleton'
import { createFileRoute } from '@tanstack/react-router'
import { Activity, Gauge, Layers, PackageX, Target, Users } from 'lucide-react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { FleetMix } from '@/features/insights/fleet-mix'
import { GmvBreakdown } from '@/features/insights/gmv-breakdown'
import { KpiCard } from '@/features/insights/kpi-card'
import { LoadsTrendChart } from '@/features/insights/loads-trend-chart'
import { RangeSelector } from '@/features/insights/range-selector'
import { SectionCard } from '@/features/insights/section-card'
import { TopLanes } from '@/features/insights/top-lanes'
import { type InsightsRange, useInsightsOverview } from '@/features/insights/use-insights'
import { VerificationFunnel } from '@/features/insights/verification-funnel'

const ZodSearch = z.object({
  range: z.enum(['7d', '30d', '90d']).default('30d'),
})

export const Route = createFileRoute('/_authenticated/insights/')({
  validateSearch: ZodSearch,
  component: InsightsPage,
})

function InsightsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Skeleton className="h-80 rounded-xl xl:col-span-2" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    </div>
  )
}

function InsightsPage() {
  const { range } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data, isPending } = useInsightsOverview(range)

  const setRange = (next: InsightsRange) => {
    navigate({ search: (prev) => ({ ...prev, range: next }) })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Insights</h1>
          <p className="text-muted-foreground text-sm">Marketplace health across the platform</p>
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </div>

      {match(isPending || !data)
        .with(true, () => <InsightsSkeleton />)
        .otherwise(() => {
          const insights = data as NonNullable<typeof data>
          const { kpis } = insights
          return (
            <div className="space-y-4 pb-6">
              {/* KPI band */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <KpiCard
                  index={0}
                  label="Active loads"
                  value={`${kpis.active_loads}`}
                  sub="Open on the marketplace now"
                  icon={Activity}
                  accent="sky"
                />
                <KpiCard
                  index={1}
                  label="Open bids"
                  value={`${kpis.open_bids}`}
                  sub="Awaiting a decision"
                  icon={Layers}
                  accent="violet"
                />
                <KpiCard
                  index={2}
                  label="New loads"
                  value={`${kpis.new_loads}`}
                  sub={`Posted in last ${range}`}
                  icon={Gauge}
                  accent="emerald"
                />
                <KpiCard
                  index={3}
                  label="Bid conversion"
                  value={`${kpis.conversion_rate}%`}
                  sub="Accepted vs placed bids"
                  icon={Target}
                  accent="amber"
                />
                <KpiCard
                  index={4}
                  label="Zero-bid loads"
                  value={`${kpis.zero_bid_rate}%`}
                  sub="Posted with no bids yet"
                  icon={PackageX}
                  accent="rose"
                />
                <KpiCard
                  index={5}
                  label="New carriers"
                  value={`${kpis.new_carriers}`}
                  sub={`Signed up in last ${range}`}
                  icon={Users}
                  accent="sky"
                />
              </div>

              {/* Trend + verification */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <SectionCard
                  index={0}
                  title="Load activity"
                  description="Posted vs booked per day"
                  className="xl:col-span-2"
                >
                  <LoadsTrendChart data={insights.loads_trend} />
                </SectionCard>
                <SectionCard index={1} title="Carrier verification" description="Review backlog">
                  <VerificationFunnel data={insights.verification} />
                </SectionCard>
              </div>

              {/* GMV + lanes + fleet */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <SectionCard index={2} title="Booked GMV" description="Accepted bids by currency">
                  <GmvBreakdown data={insights.gmv_by_currency} />
                </SectionCard>
                <SectionCard index={3} title="Top lanes" description="Busiest city pairs">
                  <TopLanes data={insights.top_lanes} />
                </SectionCard>
                <SectionCard index={4} title="Fleet mix" description="Active vehicles by type">
                  <FleetMix data={insights.fleet_mix} />
                </SectionCard>
              </div>
            </div>
          )
        })}
    </div>
  )
}
